/**
 * AWS Amplify Authentication Service
 *
 * Centralized authentication supporting email/password and OAuth (Google).
 *
 * EMAIL/PASSWORD FLOWS
 *
 * New User - Email/Password Sign Up:
 * 1. /create-account/step-1 → User enters email
 *    API: checkEmailAvailable()
 * 2. /create-account/step-2 → User enters password
 *    API: createUserV2() → Creates Cognito + DB + Twilio user
 *    Auto-login after creation
 * 3. /npi-verification → User enters NPI
 *    API: POST /v2/auth/complete-signup
 * 4. /directory
 *    API: GET /v2/users/me (loadCurrentUser)
 *
 * Existing User - Email/Password Sign In:
 * 1. /login → User enters email/password
 *    API: signIn({ username, password })
 *    Tracks sign-in method via lastSignInMethod for mobile Hub event routing
 * 2. Cognito authentication → fetchUserAttributes() API
 * 3. /directory
 *    API: GET /v2/users/me (loadCurrentUser)
 *
 * Email/Password Sign-Out:
 * - Web: amplifySignOut({ global: true }) → Redirects to Hosted UI logout
 * - Mobile: amplifySignOut({ global: false }) → Local signOut (no modal)
 *
 * GOOGLE OAUTH FLOWS
 *
 * New User - Google OAuth Sign Up:
 * 1. /login → Click "Continue with Google"
 *    API: signInWithRedirect({ provider: 'Google' })
 * 2. Google OAuth → User authenticates
 *    API: Redirect to /auth/callback
 * 3. /auth/callback
 *    API: fetchAuthSession() → Creates Cognito user only
 * 4. /npi-verification → User enters NPI
 *    API: POST /v2/auth/complete-signup → Creates DB + Twilio user
 *    NOTE: May return 409 with EMAIL_IN_USE or NPI_IN_USE if conflicts exist
 * 5. /directory
 *    API: GET /v2/users/me (loadCurrentUser)
 *
 * Existing User - Google OAuth Sign In (Auto-Link):
 * 1. /login → Click "Continue with Google"
 *    API: signInWithRedirect({ provider: 'Google' })
 *    Tracks sign-in method via lastSignInMethod for mobile Hub event routing
 * 2. Google OAuth → User authenticates
 * 3. /auth/callback
 *    API: POST /v2/auth/reconcile-provider → Auto-links if verified emails match
 *    Backend: AdminLinkProviderForUser links Google to native account
 *    Silent re-auth: prompt=none mints unified tokens with updated identities
 *    Result: Native account primary with custom:user_id; Google in identities[] array
 *    Outcome values: 'linked' (linked to native), 'npi_unverified' (new user), 'federated_only' (OAuth-only complete)
 * 4. /directory
 *    API: GET /v2/users/me (loadCurrentUser)
 *
 * Existing User - Manual Account Linking Flow (NPI/Email Conflict):
 * 1. /npi-verification → User enters NPI → Conflict detected (EMAIL_IN_USE or NPI_IN_USE)
 *    API: POST /v2/auth/complete-signup returns 409
 *    UI: Shows conflict info box with masked email
 * 2. /npi-verification → User clicks "Sign in" to link accounts
 *    API: Save linking context → Sign out and redirect to login
 * 3. /login → User has linking context from previous conflict
 *    UI: Shows NPI conflict info box
 * 4. /login → User signs in (password or Google based on authProvider)
 *    API: signIn() or signInWithRedirect({ provider: 'Google' })
 * 5. /auth/callback (if Google) or /login (if password)
 *    API: POST /v2/auth/link-provider → Link accounts
 * 6. /directory
 *    API: GET /v2/users/me (loadCurrentUser)
 *
 * COGNITO IDENTITY LINKING:
 * - After auto or manual linking, native account retains sub and custom:user_id
 * - Google identity added to identities[] array with primary: false
 * - Both authentication methods mint tokens with same sub and custom:user_id
 * - Cognito console shows native account + "External provider" entry (linked, not duplicate)
 *
 * Future - Link Intent Flow (Signed-in User Adding Google):
 * - Add link_intent=true to OAuth state
 * - On callback, call /v2/auth/link-provider
 * - Not yet implemented
 *
 * Google OAuth Sign-Out:
 * - Web: amplifySignOut({ global: true }) → Hosted UI /logout → /
 * - Mobile: POST /oauth2/revoke + AsyncStorage cleanup (7 tokens)
 *
 * KEY API ENDPOINTS:
 * - POST /v2/auth/complete-signup: Create DB user after NPI verification
 * - POST /v2/auth/reconcile-provider: Auto-link Google to native account on email match
 * - POST /v2/auth/link-provider: Manually link accounts during conflict resolution
 * - GET /v2/users/me: Fetch current user profile data
 * - POST /oauth2/revoke: Revoke refresh token (mobile sign-out)
 *
 * STORAGE:
 * - Web: Browser memory + localStorage + sessionStorage (OAuth tracking)
 * - iOS: Amplify @MemoryStorage adapter → AsyncStorage (not Keychain)
 * - Android: Amplify @MemoryStorage adapter → AsyncStorage
 * - Tokens prefixed with: CognitoIdentityServiceProvider.*
 *
 * SECURITY:
 * - OAuth mobile uses ephemeral sessions (no cross-app single sign-on)
 * - User attributes from ID token claims (no extra API calls)
 * - Refresh token revocation on mobile logout (prevents token reuse)
 * - Complete storage cleanup verified (0 tokens remaining)
 * - Tokens: Access (1 hour), ID (1 hour), Refresh (30 days, revocable)
 */

import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import '@aws-amplify/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect } from 'aws-amplify/auth';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn,
  SignInInput,
  signOut as amplifySignOut,
  updatePassword,
  updateUserAttributes,
  type FetchUserAttributesOutput,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Hub, sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import Constants from 'expo-constants';
import { GestureResponderEvent } from 'react-native';

import { clearAccountLinkingContext } from './accountLinking';
import { actAsUserIdAtom, store, authenticatedUserIdAtom } from './globalState';
import { getActiveUserSession, isOAuthUser, parseIdTokenClaims, clearUserSession } from './session';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ONE_HOUR, TWELVE_HOURS } from '@/constants/time';
import { segmentClient } from '@/util/analytics';
import {
  isExpiredRefreshTokenError,
  isRevokedTokenError,
  isUnrecoverableAccountError,
  parseAuthError,
  type ParsedAuthError,
} from '@/util/authTokens';
import { env } from '@/util/env';
import { clearImpersonation } from '@/util/impersonationStorage';
import { oneSignalLogout } from '@/util/pushNotifications';
import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';
import { clearEHRSession, isEHRSession, markEHRSession } from '@/util/smartLaunch';

/** Constants & Types */

const OAUTH_PROVIDERS = {
  GOOGLE: 'Google',
} as const;

const OAUTH_ROUTES = {
  CALLBACK: '/auth/callback',
} as const;

const SESSION_STORAGE_KEYS = {
  OAUTH_COMPLETED: 'amplify_oauth_completed',
  SIGNED_IN_TS: 'amplify_signed_in_ts',
} as const;

const USER_ATTRIBUTES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

type HubEventType = 'signedIn' | 'signedOut' | 'tokenRefresh' | 'tokenRefresh_failure';

interface HubEventData {
  event: HubEventType;
  data?: any;
}

let isSignOutInProgress = false;

let lastSignInMethod: 'oauth' | 'password' | null = null;

/** Configuration & Setup */

export const COGNITO_EXCEPTIONS: Record<string, string> = {
  EXPIRED_CODE: 'ExpiredCodeException',
  CODE_MISMATCH: 'CodeMismatchException',
  LIMIT_EXCEEDED: 'LimitExceededException',
} as const;

function sanitizeDomain(value: string): string {
  return value.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const loginDomainRaw =
  env.EXPO_PUBLIC_COGNITO_LOGIN_DOMAIN ||
  (Constants.expoConfig?.extra?.EXPO_PUBLIC_AUTH_DOMAIN as string | undefined) ||
  '';

const loginDomain = sanitizeDomain(loginDomainRaw);

const oauthScopes = ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'];

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: env.EXPO_PUBLIC_COGNITO_USER_POOL,
      userPoolClientId: env.EXPO_PUBLIC_COGNITO_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: loginDomain,
          scopes: oauthScopes,
          redirectSignIn: [
            typeof window !== 'undefined' && window.location?.origin
              ? `${window.location.origin}/auth/callback`
              : undefined,
            env.EXPO_PUBLIC_APP_WEB_DOMAIN
              ? `https://${env.EXPO_PUBLIC_APP_WEB_DOMAIN}/auth/callback`
              : undefined,
            env.EXPO_PUBLIC_AUTH_CALLBACK_URL,
          ].filter(Boolean) as string[],
          redirectSignOut: [
            typeof window !== 'undefined' && window.location?.origin
              ? `${window.location.origin}/logout`
              : undefined,
            env.EXPO_PUBLIC_APP_WEB_DOMAIN
              ? `https://${env.EXPO_PUBLIC_APP_WEB_DOMAIN}/logout`
              : undefined,
            env.EXPO_PUBLIC_AUTH_LOGOUT_URL,
          ].filter(Boolean) as string[],
          responseType: 'code',
        },
      },
    },
  },
});

/**
 * A sign-in from an EHR keeps its tokens in sessionStorage, so they end with the tab: EHR
 * workstations are shared. The flag lives in sessionStorage too, so a reload inside the EHR
 * tab picks the same storage again before Amplify reads any tokens.
 */
function applySessionTokenStorage() {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
}

if (isWeb && isEHRSession()) {
  applySessionTokenStorage();
}

/** Hub Event Listeners */

/**
 * Global Hub listener for Amplify Auth events
 *
 * Handles signedIn, signedOut, tokenRefresh, and tokenRefresh_failure events.
 * Uses global flag to prevent duplicate registrations during hot reload.
 */
if (typeof window !== 'undefined') {
  if (!(window as any).__amplifyHubListenerRegistered) {
    (window as any).__amplifyHubListenerRegistered = true;

    Hub.listen('auth', ({ payload }: { payload: HubEventData }) => {
      const { event, data } = payload;

      switch (event) {
        case 'signedIn': {
          handleSignedInEvent();

          break;
        }
        case 'signedOut': {
          handleSignedOutEvent();

          break;
        }
        case 'tokenRefresh': {
          handleTokenRefreshEvent();

          break;
        }
        case 'tokenRefresh_failure': {
          handleTokenRefreshFailureEvent(data).catch((error) => {
            console.error('[Auth] Unhandled error in token refresh failure handler:', error);
          });

          break;
        }
        default: {
          break;
        }
      }
    });
  }
}

/**
 * Handle signed in event from Amplify Hub
 *
 * On mobile, only routes to OAuth callback when the last sign-in was OAuth.
 * Password logins remain on the current route. Tracks last sign-in method via
 * lastSignInMethod flag to differentiate OAuth vs password on mobile Hub events.
 */
function handleSignedInEvent() {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Hub: signedIn',
    level: 'info',
    data: { method: lastSignInMethod },
  });

  if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
    window.sessionStorage.setItem(SESSION_STORAGE_KEYS.OAUTH_COMPLETED, 'true');

    try {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.SIGNED_IN_TS, String(Date.now()));
    } catch (error) {
      console.warn('[Auth] Failed to store OAuth timestamp:', error);
    }
  }

  if (!isWeb) {
    try {
      const router = require('expo-router').router;

      if (lastSignInMethod !== 'password') {
        router.replace(OAUTH_ROUTES.CALLBACK);
      }

      lastSignInMethod = null;
    } catch (error) {
      console.error('[Auth] Failed to navigate to callback:', error);
    }
  }
}

/**
 * Handle signed out event from Amplify Hub
 */
function handleSignedOutEvent() {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Hub: signedOut',
    level: 'info',
  });

  clearUserSession();
}

/**
 * Handle token refresh event from Amplify Hub
 */
function handleTokenRefreshEvent() {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Hub: tokenRefresh succeeded',
    level: 'info',
  });
}

/**
 * Handle revoked token by clearing local session and tracking analytics.
 *
 * Shared handler for revoked token errors across different auth flows.
 * Clears stale tokens and tracks the outcome for monitoring.
 */
export async function handleRevokedToken(error: ParsedAuthError, source: string): Promise<void> {
  const getUserIdSafely = (): string | null => {
    try {
      return store.get(authenticatedUserIdAtom) || null;
    } catch {
      return null;
    }
  };

  const userId = getUserIdSafely();

  try {
    await signOutLocal();

    segmentClient.track(ANALYTICS_EVENTS.TOKEN_REVOKED_SESSION_CLEARED, {
      errorName: error.name,
      errorCode: error.code,
      errorMessage: error.message,
      source,
      success: true,
      userId,
    });
  } catch (clearError) {
    console.error('[Auth] Failed to clear revoked token session:', clearError);

    Sentry.captureException(clearError, {
      tags: {
        source: 'handleRevokedToken',
        auth_source: source,
      },
      extra: {
        originalError: error,
        userId,
      },
    });

    segmentClient.track(ANALYTICS_EVENTS.TOKEN_REVOKED_SESSION_CLEARED, {
      errorName: error.name,
      errorCode: error.code,
      errorMessage: error.message,
      source,
      success: false,
      clearError: clearError instanceof Error ? clearError.message : String(clearError),
      userId,
    });
  }
}

/**
 * Handle token refresh failure event from Amplify Hub
 *
 * Detects revoked refresh tokens and clears local session to prevent stale
 * token errors. Uses functional error parsing and detection utilities.
 *
 * @internal Exported for unit testing only. Do not call from outside this module.
 */
export async function handleTokenRefreshFailureEvent(data: unknown) {
  try {
    const parsedError = parseAuthError(data);

    if (isRevokedTokenError(parsedError)) {
      await handleRevokedToken(parsedError, 'tokenRefresh_failure');
    } else if (isExpiredRefreshTokenError(parsedError)) {
      await signOutLocal();

      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'Refresh token expired, cleared local session',
        data: { source: 'tokenRefresh_failure' },
      });
    } else if (isUnrecoverableAccountError(parsedError)) {
      // Read before signing out: `signOutLocal` clears the atom, so capturing
      // it afterwards would report every one of these rows without a user.
      const userId = (() => {
        try {
          return store.get(authenticatedUserIdAtom) || null;
        } catch {
          return null;
        }
      })();

      // The account itself is gone or turned off, so no future refresh can
      // succeed. Without clearing the session the app keeps a signed-in shell
      // whose every request 401s and never returns the person to login.
      await signOutLocal();

      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: `Account no longer authenticatable, cleared local session: ${parsedError.message}`,
        data: { source: 'tokenRefresh_failure' },
      });

      // A dead account is a different cause from a revoked token, so it gets
      // its own event rather than reusing the revoked-token one: the alert
      // pipeline and any warehouse query need to tell the two apart. userId
      // matches the sibling calls so these rows can be joined to a user.
      segmentClient.track(ANALYTICS_EVENTS.CLEARED_SESSION_FOR_UNUSABLE_ACCOUNT, {
        errorName: parsedError.name,
        errorCode: parsedError.code,
        errorMessage: parsedError.message,
        source: 'tokenRefresh_failure',
        success: true,
        userId,
      });
    } else {
      Sentry.captureException(new Error(`Token refresh failed: ${parsedError.message}`), {
        level: 'warning',
        tags: {
          source: 'tokenRefresh_failure',
          errorName: parsedError.name ?? 'unknown',
          errorCode: parsedError.code ?? 'unknown',
        },
      });
    }
  } catch (handlerError) {
    Sentry.captureException(handlerError, {
      level: 'warning',
      tags: {
        source: 'tokenRefresh_failure_handler',
      },
      extra: {
        originalEventData: JSON.stringify(data),
      },
    });
  }
}

/**
 * Revoke refresh token via /oauth2/revoke endpoint
 */
async function revokeRefreshToken(refreshToken: string): Promise<boolean> {
  try {
    const response = await fetch(`https://${loginDomain}/oauth2/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: env.EXPO_PUBLIC_COGNITO_CLIENT_ID,
        token: refreshToken,
        token_type_hint: 'refresh_token',
      }).toString(),
    });

    if (!response.ok) {
      console.error('[Auth] Token revocation failed:', response.status);

      return false;
    }

    return true;
  } catch (error) {
    console.error('[Auth] Token revocation error:', error);

    return false;
  }
}

/** OAuth URL Building Helpers */

interface AmplifyOAuthConfig {
  domain?: string;
  redirectSignIn?: string | string[];
}

interface AmplifyCognitoConfig {
  userPoolClientId?: string;
  loginWith?: {
    oauth?: AmplifyOAuthConfig;
  };
}

interface AmplifyAuthConfig {
  Auth?: {
    Cognito?: AmplifyCognitoConfig;
  };
}

interface OAuthConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
}

/**
 * Extracts OAuth configuration from AWS Amplify config.
 *
 * Retrieves domain, client ID, and redirect URI from Amplify's configuration
 * for building OAuth authorization URLs. Falls back to default redirect URI
 * based on current window origin if not found in config.
 *
 * Returns null if configuration is missing or invalid, allowing callers to
 * handle fallback behavior (e.g., using Amplify's signInWithRedirect).
 */
export function getOAuthConfig(): OAuthConfig | null {
  const defaultRedirectUri =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/auth/callback`
      : '';

  try {
    const cfg = Amplify.getConfig() as AmplifyAuthConfig | undefined;

    const oauth = cfg?.Auth?.Cognito?.loginWith?.oauth;

    const domain = oauth?.domain || '';

    const clientId = cfg?.Auth?.Cognito?.userPoolClientId || '';

    const redirect = Array.isArray(oauth?.redirectSignIn)
      ? oauth.redirectSignIn[0]
      : oauth?.redirectSignIn;

    const redirectUri = redirect || defaultRedirectUri;

    if (!domain || !clientId || !redirectUri) {
      return null;
    }

    return { domain, clientId, redirectUri };
  } catch {
    return null;
  }
}

interface BuildOAuthUrlParams {
  domain: string;
  clientId: string;
  redirectUri: string;
  prompt: 'select_account' | 'none';
  state: string;
  identityProvider?: string;
}

/**
 * Builds OAuth 2.0 authorization URL for Cognito Hosted UI.
 *
 * Constructs the authorization endpoint URL with required OAuth parameters
 * including client ID, redirect URI, scopes, prompt behavior, and state.
 * Optionally includes identity provider parameter for direct provider selection.
 *
 * Used for both interactive sign-in (prompt=select_account) and silent
 * re-authentication (prompt=none) flows.
 */
export function buildOAuthUrl(params: BuildOAuthUrlParams): string {
  const scopeString = 'openid email profile aws.cognito.signin.user.admin';

  const searchParams = new URLSearchParams({
    client_id: params.clientId,
    response_type: 'code',
    redirect_uri: params.redirectUri,
    scope: scopeString,
    prompt: params.prompt,
    state: params.state,
  });

  if (params.identityProvider) {
    searchParams.set('identity_provider', params.identityProvider);
  }

  return `https://${params.domain}/oauth2/authorize?${searchParams.toString()}`;
}

/** Public Authentication Functions */

/**
 * Set custom:onboarding_status attribute for OAuth users
 *
 * Used to track OAuth signup state during NPI verification.
 */
export async function setOnboardingStatus(status: 'pending_npi' | ''): Promise<void> {
  try {
    await updateUserAttributes({
      userAttributes: {
        'custom:onboarding_status': status,
      },
    });
  } catch (error) {
    console.error('[Auth] Failed to set onboarding_status:', error);

    throw error;
  }
}

/** Session storage key for redirect path during OAuth flow */
export const OAUTH_REDIRECT_PATH_KEY = 'primum_oauth_redirect_path';

/**
 * Storage adapter for OAuth redirect path that works on both web and native.
 * - Web: Uses sessionStorage
 * - Native: Uses AsyncStorage
 */
const getOAuthRedirectPathStorage = () => {
  if (isWeb && typeof window !== 'undefined' && window.sessionStorage) {
    return {
      async getItem() {
        return window.sessionStorage.getItem(OAUTH_REDIRECT_PATH_KEY);
      },
      async setItem(value: string) {
        window.sessionStorage.setItem(OAUTH_REDIRECT_PATH_KEY, value);
      },
      async removeItem() {
        window.sessionStorage.removeItem(OAUTH_REDIRECT_PATH_KEY);
      },
    };
  }

  return {
    async getItem() {
      return AsyncStorage.getItem(OAUTH_REDIRECT_PATH_KEY);
    },
    async setItem(value: string) {
      return AsyncStorage.setItem(OAUTH_REDIRECT_PATH_KEY, value);
    },
    async removeItem() {
      return AsyncStorage.removeItem(OAUTH_REDIRECT_PATH_KEY);
    },
  };
};

/** Save OAuth redirect path to storage (works on web and native) */
export async function saveOAuthRedirectPath(path: string) {
  const storage = getOAuthRedirectPathStorage();

  await storage.setItem(path);
}

/** Load OAuth redirect path from storage (works on web and native) */
export async function loadOAuthRedirectPath(): Promise<string | null> {
  const storage = getOAuthRedirectPathStorage();

  return storage.getItem();
}

/** Clear OAuth redirect path from storage (works on web and native) */
export async function clearOAuthRedirectPath() {
  const storage = getOAuthRedirectPathStorage();

  await storage.removeItem();
}

/**
 * Sign in with Google via Cognito Hosted UI
 *
 * Handles UserAlreadyAuthenticatedException by redirecting to callback
 * to process existing session.
 *
 * Default behavior uses prompt=SELECT_ACCOUNT to force account selection every time.
 * If user selects the same account, Google may reuse existing session (no re-entry needed).
 *
 * Caveat: when federating to Google through Cognito, AWS strips both `prompt` and
 * `loginHint` before forwarding to accounts.google.com — they are passed to Cognito's
 * /oauth2/authorize but do not reach Google's auth endpoint. Setting them is therefore
 * a no-op for the Google chooser today; they remain here to express intent and to
 * work if the Cognito IdP config is ever changed to forward them.
 *
 * @param options - Optional OAuth parameters
 * @param options.prompt - OAuth prompt parameter ('SELECT_ACCOUNT' | 'LOGIN' | 'NONE')
 * @param options.loginHint - Email to pre-fill in Google's sign-in form
 * @param options.redirectPath - Path to redirect to after successful authentication
 */
/**
 * Amplify reports a native auth session the user closed as an OAuth error whose message is the
 * session's result type. Closing it is a choice, not a failure.
 */
function isOAuthCancel(error: unknown): boolean {
  return (
    error instanceof Error && error.name === 'OAuthSignInException' && error.message === 'canceled'
  );
}

export async function signInWithGoogle(options?: {
  prompt?: 'SELECT_ACCOUNT' | 'LOGIN' | 'NONE';
  loginHint?: string;
  redirectPath?: string;
}) {
  try {
    lastSignInMethod = 'oauth';

    const promptValue = options?.prompt || 'SELECT_ACCOUNT';

    if (options?.redirectPath) {
      await saveOAuthRedirectPath(options.redirectPath);
    }

    await signInWithRedirect({
      provider: OAUTH_PROVIDERS.GOOGLE,
      options: {
        prompt: promptValue,
        ...(options?.loginHint && { loginHint: options.loginHint }),
      },
    });
  } catch (error) {
    lastSignInMethod = null;

    if (isOAuthCancel(error)) {
      return;
    }

    if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = OAUTH_ROUTES.CALLBACK;
      } else {
        const { router } = require('expo-router');

        router.replace(OAUTH_ROUTES.CALLBACK);
      }

      return;
    }

    throw error;
  }
}

/**
 * Starts Google sign-in from a press, which nothing awaits. A failure is reported here because an
 * unawaited rejection would otherwise reach Sentry only as an anonymous unhandled rejection.
 */
export function startGoogleSignIn(
  source: string,
  options?: Parameters<typeof signInWithGoogle>[0]
): void {
  signInWithGoogle(options).catch((error: unknown) => {
    Sentry.captureException(error, {
      tags: { component: 'startGoogleSignIn', source },
      extra: { errorMessage: error instanceof Error ? error.message : String(error) },
    });
  });
}

/**
 * Authenticate user with email/password via Cognito
 */
export function login({ username, password }: SignInInput) {
  lastSignInMethod = 'password';

  const signInPromise = signIn({
    username,
    password,
  });

  signInPromise.catch(() => {
    lastSignInMethod = null;
  });

  return signInPromise;
}

/**
 * Sign out from Cognito
 *
 * OAuth Mobile:
 * - Revokes refresh token via /oauth2/revoke endpoint
 * - Clears all Cognito tokens from AsyncStorage (7 tokens)
 * - Verifies cleanup (0 tokens remaining)
 * - Returns early (no amplifySignOut call, no iOS modal)
 *
 * Web (OAuth & Email/Password):
 * - Calls amplifySignOut({ global: true }) to clear Hosted UI session
 *
 * Mobile Email/Password:
 * - Calls amplifySignOut({ global: false }) for local signOut only (no modal)
 */
async function signOutFromCognito() {
  if (!isWeb) {
    try {
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        const claims = parseIdTokenClaims(idToken);

        if (claims && isOAuthUser(claims)) {
          const allKeys = await AsyncStorage.getAllKeys();

          const cognitoKeys = allKeys.filter(
            (key) =>
              key.startsWith('CognitoIdentityServiceProvider.') ||
              key.includes('CognitoIdentityServiceProvider.')
          );

          const refreshTokenKey = allKeys.find((key) => key.toLowerCase().includes('refreshtoken'));

          const refreshToken = refreshTokenKey ? await AsyncStorage.getItem(refreshTokenKey) : null;

          if (refreshToken) {
            await revokeRefreshToken(refreshToken);
          }

          if (cognitoKeys.length > 0) {
            await AsyncStorage.multiRemove(cognitoKeys);
          }

          return;
        }
      }
    } catch (error) {
      console.warn('[Auth] Could not determine auth method:', error);
    }
  }

  try {
    return await amplifySignOut({ global: isWeb });
  } catch (error) {
    if (isWeb) {
      console.warn('[Auth] Sign out error (suppressed, redirect will complete logout):', error);
    } else {
      throw error;
    }
  }
}

/** Logout & State Management */

/**
 * Removes Twilio's own sessionStorage entries (twilsock continuation tokens,
 * cached Sync maps/documents/lists) that neither the Conversations nor Sync
 * SDK clear on their own. Left behind, they survive sign-out and can hand the
 * next signed-in user on the same browser a stale local reference to a Sync
 * object that no longer exists server-side — surfacing later as an uncaught
 * "Unique name not found" (Twilio error 54300) when the SDK tries to mutate
 * it. Matches every real key we found in the SDK bundles (all `twilio_`-
 * prefixed), so the substring match is deliberately broad rather than pinned
 * to today's exact key names.
 */
function clearTwilioSessionStorage() {
  // React Native defines a global `window` without Web Storage, so a bare
  // `typeof window` check passes on native and then throws reaching
  // sessionStorage. Guarding on the platform keeps the catch below meaning
  // "web cleanup actually failed", which is the case worth hearing about.
  if (!isWeb || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    const keys: string[] = [];

    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);

      if (key && key.toLowerCase().includes('twilio')) {
        keys.push(key);
      }
    }

    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (err) {
    // This cleanup is what stops one signed-in user's cached Sync state
    // reaching the next one on a shared browser, so a failure needs a real
    // signal rather than a console line nobody reads in production.
    Sentry.captureException(err, {
      tags: { component: 'auth', source: 'twilio-session-cleanup' },
      extra: { errorMessage: err instanceof Error ? err.message : String(err) },
    });
  }
}

/**
 * Complete logout: Cognito signOut and clear application state and reset analytics.
 *
 * Performs comprehensive cleanup:
 * - Signs out from Cognito (with appropriate global/local handling)
 * - Clears React Query cache and resets analytics
 * - Clears account linking context (silent fail - cleanup only)
 * - Resets user ID and application state
 * - Executes final callback if provided
 *
 * Optional source parameter adds context to LOG_OUT analytics event (e.g., 'npi_verification').
 */
export async function signOut(
  finalCallback?: null | ((event: GestureResponderEvent) => void) | undefined | any,
  source?: string
) {
  isSignOutInProgress = true;

  clearEHRSession();

  try {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Log out user and reset app state',
      level: 'info',
      data: {
        hasCallback: !!finalCallback,
        source,
        steps: ['cognito_signout', 'clear_query_cache', 'reset_segment_analytics', 'clear_user_id'],
      },
    });

    try {
      const { claims, isOAuth } = await getActiveUserSession();

      const providerName = Array.isArray(claims?.identities)
        ? String(claims!.identities![0]?.providerName || '').toLowerCase()
        : undefined;

      segmentClient.track(ANALYTICS_EVENTS.LOG_OUT, {
        method: isOAuth ? 'oauth' : 'password',
        provider: providerName,
        ...(source ? { source } : {}),
      });
    } catch {}

    clearUserAttributesCache();

    oneSignalLogout();

    store.set(authenticatedUserIdAtom, undefined);

    store.set(actAsUserIdAtom, undefined);

    await clearImpersonation();

    await signOutFromCognito();
  } catch (e) {
    console.error('[Auth] Logout failed:', e);

    Sentry.captureException(e);
  } finally {
    queryClient.removeQueries();

    clearTwilioSessionStorage();

    clearAccountLinkingContext().catch((err) => {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Failed to clear account linking context during logout',
        level: 'warning',
        data: { error: err instanceof Error ? err.message : String(err) },
      });
    });

    if (typeof finalCallback === 'function') {
      finalCallback?.();
    }

    const resetLogoutFlag = () => {
      isSignOutInProgress = false;
    };

    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => setTimeout(resetLogoutFlag, 1000));
    } else {
      setTimeout(resetLogoutFlag, 1000);
    }
  }
}

/**
 * Check if sign out is in progress (prevents 401 handler double-logout)
 */
export function isSigningOut(): boolean {
  return isSignOutInProgress;
}

/**
 * Local sign out (no Hosted UI redirect)
 *
 * Used during account linking to clear local session before signing in
 * with a different account. Prevents auto-logout handler from triggering.
 *
 * On web, clears localStorage/sessionStorage keys directly to avoid Hosted UI flows.
 *
 * On native, clears AsyncStorage Cognito keys directly instead of calling amplifySignOut
 * to preserve tokens needed for account linking. The duplicateIdToken saved in linking
 * context must remain valid for the /v2/auth/link-provider API call after password login.
 * Calling amplifySignOut would invalidate all tokens including the saved duplicateIdToken,
 * causing linking to fail with "Unable to link" errors.
 *
 * Mirrors the delayed reset used in full signOut().
 */
export async function signOutLocal() {
  try {
    isSignOutInProgress = true;

    pendingTokenRequest = null;

    clearUserAttributesCache();

    store.set(authenticatedUserIdAtom, undefined);

    store.set(actAsUserIdAtom, undefined);

    await clearImpersonation();

    if (isWeb) {
      if (typeof window !== 'undefined') {
        try {
          const clearKeys = (storage: Storage) => {
            const keys = [] as string[];

            for (let i = 0; i < storage.length; i++) {
              const key = storage.key(i);

              if (key && key.includes('CognitoIdentityServiceProvider')) {
                keys.push(key);
              }
            }

            keys.forEach((k) => storage.removeItem(k));
          };

          clearKeys(window.localStorage);

          clearKeys(window.sessionStorage);

          clearTwilioSessionStorage();
        } catch (err) {
          console.warn('[Auth] Web local sign out cleanup failed:', err);
        }
      }
    } else {
      try {
        const allKeys = await AsyncStorage.getAllKeys();

        const cognitoKeys = allKeys.filter(
          (key) =>
            key.startsWith('CognitoIdentityServiceProvider.') ||
            key.includes('CognitoIdentityServiceProvider.')
        );

        if (cognitoKeys.length > 0) {
          await AsyncStorage.multiRemove(cognitoKeys);
        }
      } catch (err) {
        console.warn('[Auth] Native local sign out cleanup failed:', err);
      }
    }
  } catch (e) {
    console.warn('[Auth] Local sign out failed:', e);
  } finally {
    const resetFlag = () => {
      isSignOutInProgress = false;
    };

    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => setTimeout(resetFlag, 1000));
    } else {
      setTimeout(resetFlag, 1000);
    }
  }
}

/**
 * Get current user's ID token for API authentication
 *
 * Deduplicates concurrent calls so that only one fetchAuthSession() is in-flight
 * at a time. When multiple API requests fire simultaneously (e.g., on app foreground
 * when React Query refetches ~8 queries at once), all callers share the same
 * pending promise instead of each independently hitting Cognito.
 *
 * This prevents a thundering-herd of token refresh requests that can trigger
 * rate limiting or cascade failures when the access token is expired.
 */
let pendingTokenRequest: Promise<string | null> | null = null;

export function getIdToken(): Promise<string | null> {
  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  pendingTokenRequest = fetchIdToken().finally(() => {
    pendingTokenRequest = null;
  });

  return pendingTokenRequest;
}

async function fetchIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();

    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        source: 'getIdToken',
      },
      extra: {
        userPoolId: env.EXPO_PUBLIC_COGNITO_USER_POOL,
        clientId: env.EXPO_PUBLIC_COGNITO_CLIENT_ID,
        loginDomain,
        isSignOutInProgress,
        hasUserIdInStore: !!store.get(authenticatedUserIdAtom),
        platform: isWeb ? 'web' : 'native',
      },
      level: 'error',
    });

    throw error;
  }
}

/**
 * User attributes cache to avoid repeated Cognito API calls
 */
let userAttributesCache: {
  userId: string | undefined;
  attributes: FetchUserAttributesOutput;
  expiresAt: number;
} | null = null;

/**
 * Get user attributes (OAuth: from ID token claims, Email/Password: from Cognito API)
 *
 * Caches attributes for 5 minutes to avoid repeated Cognito API calls.
 */
export async function getUserAttributes(): Promise<FetchUserAttributesOutput> {
  const now = Date.now();

  const currentUserId = store.get(authenticatedUserIdAtom);

  if (
    userAttributesCache &&
    userAttributesCache.expiresAt > now &&
    userAttributesCache.userId === currentUserId
  ) {
    return userAttributesCache.attributes;
  }

  const { attributes } = await getActiveUserSession();

  // Cache for 5 minutes
  userAttributesCache = {
    userId: currentUserId,
    attributes: attributes || {},
    expiresAt: now + USER_ATTRIBUTES_CACHE_TTL,
  };

  return userAttributesCache.attributes;
}

/**
 * Invalidates the user attributes cache to ensure most recent user attributes fetched for Cognito API
 */
export function clearUserAttributesCache() {
  userAttributesCache = null;
}

/** React Query Integration */

export const getUserAttributesQueryOptions = () => ({
  queryKey: ['userAttributes'],
  queryFn: getUserAttributes,
  staleTime: TWELVE_HOURS,
  gcTime: ONE_HOUR,
});

/** Magic Link Authentication */

/**
 * Authenticate user with a magic link token via Cognito Custom Auth.
 *
 * Flow:
 * 1. Initiate CUSTOM_WITHOUT_SRP auth with clientMetadata.action="verify"
 *    (tells CreateAuthChallenge Lambda to skip token generation/email)
 * 2. Respond to the custom challenge with the token from the magic link URL
 * 3. Cognito verifies token via VerifyAuthChallengeResponse Lambda (DynamoDB lookup)
 *
 * Sets lastSignInMethod to 'password' to prevent the Hub listener from
 * redirecting to /auth/callback on mobile.
 */
export async function signInWithMagicLink(email: string, token: string) {
  lastSignInMethod = 'password';

  try {
    const signInResult = await signIn({
      username: email,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
        clientMetadata: {
          action: 'verify',
        },
      },
    });

    if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      return await confirmSignIn({
        challengeResponse: token,
      });
    }

    return signInResult;
  } catch (error) {
    lastSignInMethod = null;

    throw error;
  }
}

/**
 * Redeem a single-use SMART login token minted by the API after an EHR launch.
 *
 * The username is the Cognito sub, because EHR-only and Google-only accounts have no
 * address to sign in by. The challenge carries the binding this tab created at launch, which
 * proves the browser finishing sign-in is the one that started it. No clientMetadata: the
 * challenge Lambda skips minting and emailing for the web client regardless.
 */
export async function signInWithSmartToken(sub: string, code: string, binding: string) {
  lastSignInMethod = 'password';

  markEHRSession();

  applySessionTokenStorage();

  try {
    const signInResult = await signIn({
      username: sub,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });

    if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      return await confirmSignIn({ challengeResponse: `smart:${code}:${binding}` });
    }

    return signInResult;
  } catch (error) {
    lastSignInMethod = null;

    throw error;
  }
}

/** Re-exports */

export {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  updatePassword,
};
